import { z } from 'zod';
import { patchSchema } from './common.ts';

/**
 * Device registry ("Qurilma boshqaruvi").
 * category (Qurilma turkumi) → device (Qurilma) placed at a location (Qurilma joylashgan o'rni),
 * a device exposes indicators (Ko'rsatkichlar): physical ones come from the unified server,
 * virtual ones are computed by a formula from other indicators.
 */

export const hexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const createDeviceCategorySchema = z.object({
  name: z.string().trim().min(1).max(160),
  icon: z.string().trim().max(64).nullish(),
  color: hexColorSchema.nullish(),
  description: z.string().trim().max(2000).nullish(),
});
export const updateDeviceCategorySchema = patchSchema(createDeviceCategorySchema);

export interface DeviceCategoryDto {
  id: string;
  tenantId: string;
  name: string;
  icon: string | null;
  color: string | null;
  description: string | null;
  deviceCount: number;
  createdBy: { id: string; fullName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export const createDeviceLocationSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).nullish(),
});
export const updateDeviceLocationSchema = patchSchema(createDeviceLocationSchema);

export interface DeviceLocationDto {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  deviceCount: number;
  createdBy: { id: string; fullName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export const DEVICE_STATUSES = ['active', 'inactive', 'maintenance'] as const;
export type DeviceStatus = (typeof DEVICE_STATUSES)[number];

/** Calibration passport of a device ("KOLIBROVKA PASPORTI") — the uploaded file's metadata. */
export interface DevicePassportDto {
  id: string;
  name: string;
  mime: string;
  size: number;
  url: string;
  createdAt: string;
}

export const createDeviceSchema = z.object({
  name: z.string().trim().min(1).max(200),
  code: z.string().trim().min(1).max(64),
  categoryId: z.uuid().nullish(),
  locationId: z.uuid().nullish(),
  serialNumber: z.string().trim().max(128).nullish(),
  model: z.string().trim().max(128).nullish(),
  status: z.enum(DEVICE_STATUSES).default('active'),
  /** Device id in the unified server. */
  externalId: z.string().trim().max(128).nullish(),
  description: z.string().max(20_000).nullish(),
  installedAt: z.iso.date().nullish(),
  passportFileId: z.uuid().nullish(),
});
export const updateDeviceSchema = patchSchema(createDeviceSchema);
export type CreateDeviceInput = z.infer<typeof createDeviceSchema>;

export interface DeviceDto {
  id: string;
  tenantId: string;
  name: string;
  code: string;
  category: { id: string; name: string; color: string | null; icon: string | null } | null;
  source: string;
  sourceName: string | null;
  passportFileId: string | null;
  /** metadata of `passportFileId` (null when there is none or the file was removed) */
  passport?: DevicePassportDto | null;
  lastReadingAt: string | null;
  location: { id: string; name: string } | null;
  serialNumber: string | null;
  model: string | null;
  status: (typeof DEVICE_STATUSES)[number];
  externalId: string | null;
  description: string | null;
  installedAt: string | null;
  indicators: IndicatorDto[];
  /** "Qo'shgan xodim" */
  createdBy?: { id: string; fullName: string } | null;
  createdAt: string;
  updatedAt: string;
}

export const INDICATOR_KINDS = ['physical', 'virtual'] as const;

/** "DIAGRAMMA TURI" (03-modules §4.6): Random = `auto`, Chiziqli, Ustunli, Maydonli, Nuqtali, Pog'onali. */
export const INDICATOR_CHART_TYPES = ['auto', 'line', 'bar', 'area', 'scatter', 'step'] as const;
export type IndicatorChartType = (typeof INDICATOR_CHART_TYPES)[number];

export const thresholdsSchema = z.object({
  warningLow: z.number().nullish(),
  warningHigh: z.number().nullish(),
  alarmLow: z.number().nullish(),
  alarmHigh: z.number().nullish(),
});
export type Thresholds = z.infer<typeof thresholdsSchema>;

export const createIndicatorSchema = z.object({
  name: z.string().trim().min(1).max(120),
  code: z.string().trim().min(1).max(64),
  unit: z.string().trim().max(32).nullish(),
  kind: z.enum(INDICATOR_KINDS).default('physical'),
  /** Channel/sensor id in the unified server (physical indicators). */
  externalChannelId: z.string().trim().max(128).nullish(),
  /** Expression over other indicators of the tenant, e.g. `{P201.level} - {P202.level}` (virtual indicators). */
  formula: z.string().trim().max(2000).nullish(),
  precision: z.number().int().min(0).max(6).default(2),
  thresholds: thresholdsSchema.nullish(),
  sourceIndicatorId: z.uuid().nullish(),
  chartType: z.enum(INDICATOR_CHART_TYPES).default('auto'),
  color: hexColorSchema.nullish(),
  alarmEnabled: z.boolean().default(false),
});
export const updateIndicatorSchema = patchSchema(createIndicatorSchema);

export interface IndicatorDto {
  id: string;
  deviceId: string;
  name: string;
  code: string;
  unit: string | null;
  kind: (typeof INDICATOR_KINDS)[number];
  externalChannelId: string | null;
  formula: string | null;
  precision: number;
  thresholds: Thresholds | null;
  sourceIndicatorId: string | null;
  chartType: string;
  color: string | null;
  alarmEnabled: boolean;
  lastValue: number | null;
  lastValueAt: string | null;
}

/* ───────────── unified-server connector ("Konnektorlar boshqaruvi") ───────────── */

/** Channel keys of the unified-server connector: `indicator.externalChannelId = 'hydro:<KEY>'`. */
export const CONNECTOR_HYDRO_KEYS = ['LEVEL', 'VOLUME', 'INFLOW', 'DISCHARGE'] as const;
export const CONNECTOR_CHANNEL_PREFIX = 'hydro:';

/** `GET /tenants/:t/connector-status` — the live data source of the API and this tenant's binding summary. */
export interface ConnectorStatusDto {
  /** source running in the API (`LIVE_SOURCE`): `mock` (simulated values), `unified` (ATNT unified server) or null */
  source: 'mock' | 'unified' | null;
  /** configured source, even when it could not start (e.g. `unified` without a base URL / API key) */
  configured: 'mock' | 'unified';
  health: { ok: boolean; lastSuccessAt: string | null; lastError: string | null } | null;
  /** poll / tick period of the source in ms */
  intervalMs: number | null;
  /** this tenant's unified-server site code (`tenants.externalSiteId`) */
  externalSiteId: string | null;
  physicalIndicators: number;
  /** physical indicators whose `externalChannelId` is a valid `hydro:<KEY>` */
  boundIndicators: number;
}
